import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, Copy, Check } from "lucide-react";
import { fetchAiProviders, fetchPost, summarizeArticle } from "../api";
import type { Post } from "../types";
import { Markdown } from "../components/Markdown";
import { ArticleOutline } from "../components/ArticleOutline";
import { ArticleCommunity } from "../components/ArticleCommunity";
import { useAuth } from "../auth";
import { AiProviderIcon } from "../components/AiProviderIcon";
import type { AiProvider } from "../api";

export function PostDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const id = Number(searchParams.get("id"));
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiReady, setAiReady] = useState(false);
  const [aiChecked, setAiChecked] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiError, setAiError] = useState("");
  const [activeAi, setActiveAi] = useState<AiProvider | null>(null);
  const [summaryRun, setSummaryRun] = useState(0);
  const summaryRef = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchPost(id)
      .then(setPost)
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { if (!user) { setAiReady(false); setActiveAi(null); setAiChecked(true); return; } setAiChecked(false); fetchAiProviders().then(result => { const active = result.providers.find(item => item.key === result.enabled_provider) || null; setActiveAi(active); setAiReady(Boolean(active?.logged_in)); }).catch(() => { setActiveAi(null); setAiReady(false); }).finally(() => setAiChecked(true)); }, [user]);
  useEffect(() => { if (summaryRun) requestAnimationFrame(() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })); }, [summaryRun]);

  const summarize = async () => { setSummarizing(true); setAiError(""); try { setAiSummary((await summarizeArticle(id)).reply); } catch (reason) { setAiError(reason instanceof Error ? reason.message : "AI 总结失败"); } finally { setSummarizing(false); setSummaryRun(value=>value+1); } };

  const copyContent = async () => {
    if (!post?.content) return;
    try {
      await navigator.clipboard.writeText(post.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = post.content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-[var(--text-tertiary)]">
        <Loader2 className="w-10 h-10 animate-spin mb-4 text-primary" />
        <p className="text-sm">加载中...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-[var(--text-secondary)]">文章不存在或已删除</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-16 lg:grid lg:grid-cols-[220px_minmax(0,760px)] lg:justify-center lg:gap-10 items-start">
      <div className="hidden lg:block sticky top-24"><ArticleOutline content={post.content || ""} scope="post" /></div>
      <main className="min-w-0">
        <button
        onClick={() => window.history.back()}
        className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-primary-light mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        返回
      </button>
      <div className="flex items-start justify-between gap-5 mb-6"><h1 className="min-w-0 text-3xl sm:text-4xl font-bold text-[var(--text-primary)]">{post.title}</h1><div className="flex items-center gap-2 shrink-0"><button type="button" onClick={copyContent} className="h-10 px-4 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] inline-flex items-center gap-2 text-sm hover:bg-[var(--bg-surface-hover)] transition-colors" title="复制文章内容">{copied ? <><Check className="w-4 h-4 text-green-500" /><span className="text-green-500">已复制</span></> : <><Copy className="w-4 h-4" /><span>复制</span></>}</button><button type="button" onClick={() => void summarize()} disabled={!aiReady || !aiChecked || summarizing} title={!user ? "登录后使用自己的 AI 提炼" : aiChecked && !aiReady ? "请先在 AI 设置中启用并登录一个 AI 来源" : "每次提炼都会创建新会话"} className="h-10 px-4 rounded-lg bg-primary text-white inline-flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">{summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AiProviderIcon provider={activeAi?.key} name={activeAi?.name} className="w-4 h-4" />}{aiSummary ? "重新提炼" : "AI 提炼"}</button></div></div>
      {aiChecked && user && !aiReady && <button type="button" onClick={() => navigate("/settings?tab=ai")} className="block ml-auto -mt-4 mb-5 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">未启用 AI，前往设置</button>}
      {post.author && (
        <div className="flex items-center gap-3 mb-10 text-sm text-[var(--text-tertiary)]">
          <button onClick={() => post.userId && navigate(`/users/${post.userId}`)} className="inline-flex items-center gap-3 font-medium text-[var(--text-secondary)] hover:text-primary-light" aria-label={`查看 ${post.author} 的主页`}><span className="inline-flex items-center justify-center w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-primary to-accent text-white text-xs font-bold">{post.authorAvatar?<img src={post.authorAvatar} alt="" className="w-full h-full object-cover"/>:post.author.charAt(0).toUpperCase()}</span>{post.author}</button>
          {post.createdAt && <span>· {post.createdAt.slice(0, 10)}</span>}
          {post.userId&&<button onClick={()=>navigate(`/users/${post.userId}/collections/${post.collectionId||"unclassified"}`)} className="hover:text-primary-light">· 合集：{post.collectionName||"未归类"}</button>}
        </div>
      )}
      <article className="prose prose-lg max-w-none text-[var(--text-primary)]">
        <Markdown scope="post">{post.content || "暂无内容"}</Markdown>
      </article>
      {(aiSummary || aiError) && <section ref={summaryRef} className="scroll-mt-24 mt-10 rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-5 sm:p-6"><h2 className="font-bold flex items-center gap-2"><AiProviderIcon provider={activeAi?.key} name={activeAi?.name} className="w-5 h-5" />{activeAi?.name || "AI"} 提炼结果</h2>{aiError && <p className="mt-4 text-sm text-rose">{aiError}</p>}{aiSummary && <div className="mt-5 pt-5 border-t border-[var(--border-subtle)]"><Markdown scope="ai-summary">{aiSummary}</Markdown></div>}</section>}
      <ArticleCommunity documentId={Number(post.id)} />
      </main>
    </div>
  );
}
